import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { useJoin } from "./api/auth.mutations";
import { inviteStatusQueryOptions } from "./api/invite.queries";
import { type JoinInput, type JoinValues, joinSchema } from "./auth.schemas";
import { CredentialForm } from "./CredentialForm";

const route = getRouteApi("/join/$token");

export function JoinPage() {
  const { token } = route.useParams();
  const { data: status } = useSuspenseQuery(inviteStatusQueryOptions(token));

  // Invalid/expired/revoked/unknown token: no form.
  if (!status.valid) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center text-foreground">
        <p className="text-xl font-semibold">
          Inbjudningslänken är ogiltig eller har gått ut
        </p>
      </div>
    );
  }

  return <JoinForm token={token} />;
}

function JoinForm({ token }: { token: string }) {
  const form = useForm<JoinInput, unknown, JoinValues>({
    resolver: zodResolver(joinSchema),
    defaultValues: { username: "", password: "" },
  });
  const join = useJoin();
  const navigate = useNavigate();
  const { showApiError } = useErrorDialog();

  const onSubmit = (values: JoinValues) =>
    join.mutate(
      { ...values, token },
      {
        // New accounts are applicants; the app-shell guard routes them to /pending.
        onSuccess: () => navigate({ to: "/" }),
        onError: (err) =>
          showApiError(err, { title: "Registreringen misslyckades" }),
      },
    );

  return (
    <CredentialForm
      form={form}
      onSubmit={onSubmit}
      heading="Skapa konto"
      submitLabel="Skapa konto"
      pending={join.isPending}
    />
  );
}
